import { useEffect } from 'react';

import {
  BASE_URL,
  SITE_NAME,
  breadcrumbJsonLd,
  breadcrumbTrail,
  ogImageAltFor,
  ogImageFor,
} from '../config/seoShared';

// Describes the actual default share card (public/og-image.jpg).
const DEFAULT_IMAGE_ALT =
  'E.A.T. card: We build IT teams that actually deliver. CampusToCorp IT internship and corporate readiness program, Belgaum and Bangalore.';

// Updates an existing <meta>/<link> tag's value attribute in place. Every
// tag this touches already exists in public/index.html, so this never needs
// to create elements - just overwrite the ones already there.
function setTag(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

// react-helmet-async never actually commits to the DOM in this app's setup
// (confirmed across both 2.x and 3.x, in dev and production - a trivial
// <Helmet><title>...</Helmet> with nothing else in the tree also fails to
// update document.title). Rather than depend on a broken library, this
// updates the document head directly. Runs in an effect (post-commit),
// which is the React-sanctioned place for DOM side effects.
export default function PageSEO({
  title,
  description,
  ogDescription, // optional: pitch-style text for link previews; falls back to `description`
  path = '/',
  image, // defaults to this page's own card (see seoShared.js)
  imageAlt,
  keywords,
  exactTitle = false, // use `title` verbatim instead of appending " | E.A.T."
  noindex = false, // keep the page out of search results but let crawlers follow its links
  breadcrumbName, // names a dynamic page (a job) that has no breadcrumb entry of its own
  jsonLd, // extra structured data: one object or an array
}) {
  const jsonLdText = JSON.stringify(jsonLd || null);

  useEffect(() => {
    const image_ = image || ogImageFor(path);
    const imageAlt_ = imageAlt || ogImageAltFor(path) || DEFAULT_IMAGE_ALT;
    const fullTitle = exactTitle || title === 'E.A.T.' ? title : `${title} | E.A.T.`;
    const canonical = `${BASE_URL}${path}`;
    const social = ogDescription || description;

    document.title = fullTitle;
    setTag('meta[name="description"]', 'content', description);
    setTag('link[rel="canonical"]', 'href', canonical);
    setTag('meta[property="og:site_name"]', 'content', SITE_NAME);
    setTag('meta[property="og:title"]', 'content', fullTitle);
    setTag('meta[property="og:description"]', 'content', social);
    setTag('meta[property="og:url"]', 'content', canonical);
    setTag('meta[property="og:image"]', 'content', image_);
    setTag('meta[property="og:image:alt"]', 'content', imageAlt_);
    setTag('meta[property="og:type"]', 'content', 'website');
    setTag('meta[name="twitter:title"]', 'content', fullTitle);
    setTag('meta[name="twitter:description"]', 'content', social);
    setTag('meta[name="twitter:image"]', 'content', image_);
    setTag('meta[name="twitter:image:alt"]', 'content', imageAlt_);

    // Keywords are optional. The site-wide value is restored when leaving the page.
    const keywordsTag = document.querySelector('meta[name="keywords"]');
    const previousKeywords = keywordsTag?.getAttribute('content');
    if (keywords && keywordsTag) keywordsTag.setAttribute('content', keywords);

    const robotsTag = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsTag?.getAttribute('content');
    if (noindex && robotsTag) robotsTag.setAttribute('content', 'noindex, follow');

    // Breadcrumb trail plus any extra structured data, removed again on leaving the page.
    const blocks = [];
    const trail = breadcrumbTrail(path, breadcrumbName);
    if (trail.length) blocks.push(breadcrumbJsonLd(trail));
    const extra = JSON.parse(jsonLdText);
    if (extra) blocks.push(...(Array.isArray(extra) ? extra : [extra]));
    const scripts = blocks.map((block) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.dataset.pageSeo = 'true';
      el.text = JSON.stringify(block);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      if (keywords && keywordsTag && previousKeywords != null) {
        keywordsTag.setAttribute('content', previousKeywords);
      }
      if (noindex && robotsTag && previousRobots != null) {
        robotsTag.setAttribute('content', previousRobots);
      }
      scripts.forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    ogDescription,
    path,
    image,
    imageAlt,
    keywords,
    exactTitle,
    noindex,
    breadcrumbName,
    jsonLdText,
  ]);

  return null;
}
